<template>
  <tour-wizard :tour="tour" v-if="tour"></tour-wizard>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import TourWizard from './TourWizard'

  export default {
    name: 'tour-wizard-wrapper',
    data () {
      return {
        id: null,
        tours: [
          {
            id: 'manager-onboarding-tour',
            name: 'Onboarding Tour',
            endpoint: {
              type: 'internal',
              url: '/dashboard'
            },
            navigation: {
              previousBtnText: 'Previous step',
              nextBtnText: 'Next step'
            },
            slides: [
              {
                title: 'Let’s get you started',
                text: '<p>We’ll help you set the direction and we’ll be there to support you throughout your entire ED&I Journey.</p><p>Before we discuss your strategy, please take a moment to discover the tools and the resources that you can use.</p>',
                media: {
                  type: 'image',
                  alt: 'Graphic scheme illustrating the steps and workflow of the Clear Assured web application',
                  resource: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Dashboard/Dashboard_1_Intro.jpg'
                }
              },
              {
                title: 'Here’s your dashboard',
                text: '<p>It displays your assignments, newly added resources and a notification area with system updates.</p>',
                media: {
                  type: 'video',
                  title: 'Sections in Dashboard',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Dashboard/Dashboard_2_sections.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s have a look at the tasks',
                text: '<p>These are all the actions you may need to complete as part of your ED&I journey, although not all of them are relevant to your organisation or your current strategy.</p>',
                media: {
                  type: 'video',
                  title: 'Tasks list',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Dashboard/Dashboard_3_Tasks.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Here\'s your backlog',
                text: '<p>Before starting to work on the tasks you will meet your account manager and together you will create an overall strategy and set up an action plan: set your direction, choose the first tasks to work on and bring them to the focus area.</p>',
                media: {
                  type: 'video',
                  title: 'Set the focus area',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TasksList/TasksList_1_Direction.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Manage your focus area',
                text: '<p>See what everyone is up to or focus on your own assignments. You can change a task\'s status by dragging it across columns or from the detailed view.</p><p>Remember to wait for your first strategy call before dragging tasks into your focus area. Your account manager will help you prioritize the most important tasks to start with.</p>',
                media: {
                  type: 'video',
                  title: 'Check out a task',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TasksList/TasksList_2_StartWorking.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Start working on your first task',
                text: '<p>Go through the task description and access the related resources to understand what needs to be done.</p>',
                media: {
                  type: 'video',
                  title: 'Task content',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TaskSingle/TaskSingle_1_ReadDetails.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Collaborate with your team',
                text: '<p>Take notes, log progress, assign it to a colleague or submit it for our review. Your progress will be stored in the task history.</p>',
                media: {
                  type: 'video',
                  title: 'Update task progress',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TaskSingle/TaskSingle_2_UpdateProgress.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s have a look at the Inclusion Hub',
                text: '<p>The one-stop source of diversity and inclusion resources, accessible to anyone in your organisation. Use the Inclusion Hub to upskill your workforce on all things ED&I.</p>',
                media: {
                  type: 'video',
                  title: 'Inclusion hub',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TaskSingle/TaskSingle_3_MenuCat-InclusionHub.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Find the resources you need',
                text: '<p>Start typing to search. Filter by topic, category or resource type to narrow down the list of results.</p>',
                media: {
                  type: 'video',
                  title: 'Search the inclusion hub',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/InclusionHub/InclusionHub_1_Search.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Explore our resources',
                text: '<p>Take a deep dive into a topic or explore resources to get a feel for what you need.</p><p>Get in touch and share your ideas with us. We are always reviewing and updating our content.</p>',
                media: {
                  type: 'video',
                  title: 'Filter the inclusion hub by category or topic',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/InclusionHub/InclusionHub_2_Filter.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s jump into an individual resource',
                text: '<p>Not what you need? Get in touch and let us know. We value your feedback.</p>',
                media: {
                  type: 'video',
                  title: 'Inclusion hub article',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/InclusionHub/InclusionHub_3_AccessAnArticle.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Individual resources simple and concise',
                text: '<p>The enclosed information will provide the answers and the related resources will provide the context.</p>',
                media: {
                  type: 'video',
                  title: 'Article content',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/ArticleSingle/Article_1_BrowseContent.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Ready to share these resources with your colleagues?',
                text: '<p>Let’s see how we can bring them onboard.</p>',
                media: {
                  type: 'video',
                  title: 'Company users list',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/ArticleSingle/Article_2_MenuCat-Users.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s have a look at your team',
                text: '<p>Here are the users in your organisation. You can search by name and email address, filter by title, role or status and order by creation and last login date.</p>',
                media: {
                  type: 'video',
                  title: 'Browse users list',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Users/UsersList.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Bring in your team',
                text: '<p>Invite your team to progress your ED&I journey.</p><p>There are 2 types of users: company admins and regular users. Company admins will have the same privileges as you have. Regular users will not be allowed to manage users, change the company profile or access the billing settings. </p>',
                media: {
                  type: 'video',
                  title: 'Invite users',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Users/InviteUsers.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Manage team members',
                text: '<p>Find the relevant team member and click anywhere in the table row to access their profile.</p>',
                media: {
                  type: 'video',
                  title: 'User profile',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/UserProfile/UserProfile-1-SeeProfile.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Answer help calls',
                text: '<p>Your colleagues may need your help to manage their accounts. As an organisation administrator, you can manage their login credentials and their profile information.</p>',
                media: {
                  type: 'video',
                  title: 'Edit user’s details',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/UserProfile/UserProfile-2-EditUser.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'You are now ready to explore the Clear Assured and start planning your ED&I journey.',
                text: '<p>Thank you for taking the time to get familiar with the application.</p>',
                media: {
                  type: 'image',
                  alt: 'Random cheerful image (the one in the design)',
                  resource: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TourCompleted/TourComplete.jpeg'
                }
              }
            ]
          },
          {
            id: 'regular-onboarding-tour',
            name: 'Onboarding Tour',
            endpoint: {
              type: 'internal',
              url: '/dashboard'
            },
            navigation: {
              previousBtnText: 'Previous step',
              nextBtnText: 'Next step'
            },
            slides: [
              {
                title: 'Let’s get you started',
                text: '<p>We’ll help you set the direction and we’ll be there to support you throughout your entire ED&I Journey.</p><p>Before we discuss your strategy, please take a moment to discover the tools and the resources that you can use.</p>',
                media: {
                  type: 'image',
                  alt: 'Graphic scheme illustrating the steps and workflow of the Clear Assured web application',
                  resource: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Dashboard/Dashboard_1_Intro.jpg'
                }
              },
              {
                title: 'Here’s your dashboard',
                text: '<p>It displays your assignments, newly added resources and a notification area with system updates.</p>',
                media: {
                  type: 'video',
                  title: 'Sections in Dashboard',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Dashboard/Dashboard_2_sections.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s have a look at the tasks',
                text: '<p>These are all the actions you may need to complete as part of your ED&I journey, although not all of them are relevant to your organisation or your current strategy.</p>',
                media: {
                  type: 'video',
                  title: 'Tasks list',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/Dashboard/Dashboard_3_Tasks.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Here\'s your backlog',
                text: '<p>Before starting to work on the tasks you will meet your account manager and together you will create an overall strategy and set up an action plan: set your direction, choose the first tasks to work on and bring them to the focus area.</p>',
                media: {
                  type: 'video',
                  title: 'Set the focus area',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TasksList/TasksList_1_Direction.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Manage your focus area',
                text: '<p>See what everyone is up to or focus on your own assignments. You can change a task\'s status by dragging it across columns or from the detailed view.</p><p>Remember to wait for your first strategy call before dragging tasks into your focus area. Your account manager will help you prioritize the most important tasks to start with.</p>',
                media: {
                  type: 'video',
                  title: 'Check out a task',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TasksList/TasksList_2_StartWorking.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Start working on your first task',
                text: '<p>Go through the task description and access the related resources to understand what needs to be done.</p>',
                media: {
                  type: 'video',
                  title: 'Task content',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TaskSingle/TaskSingle_1_ReadDetails.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Collaborate with your team',
                text: '<p>Take notes, log progress, assign it to a colleague or submit it for our review. Your progress will be stored in the task history.</p>',
                media: {
                  type: 'video',
                  title: 'Update task progress',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TaskSingle/TaskSingle_2_UpdateProgress.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s have a look at the Inclusion Hub',
                text: '<p>The one-stop source of diversity and inclusion resources, accessible to anyone in your organisation. Use the Inclusion Hub to upskill your workforce on all things ED&I.</p>',
                media: {
                  type: 'video',
                  title: 'Inclusion hub',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TaskSingle/TaskSingle_3_MenuCat-InclusionHub.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Find the resources you need',
                text: '<p>Start typing to search. Filter by topic, category or resource type to narrow down the list of results.</p>',
                media: {
                  type: 'video',
                  title: 'Search the inclusion hub',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/InclusionHub/InclusionHub_1_Search.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Explore our resources',
                text: '<p>Take a deep dive into a topic or explore resources to get a feel for what you need.</p><p>Get in touch and share your ideas with us. We are always reviewing and updating our content.</p>',
                media: {
                  type: 'video',
                  title: 'Filter the inclusion hub by category or topic',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/InclusionHub/InclusionHub_2_Filter.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Let’s jump into an individual resource',
                text: '<p>Not what you need? Get in touch and let us know. We value your feedback.</p>',
                media: {
                  type: 'video',
                  title: 'Inclusion hub article',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/InclusionHub/InclusionHub_3_AccessAnArticle.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'Individual resources simple and concise',
                text: '<p>The enclosed information will provide the answers and the related resources will provide the context.</p>',
                media: {
                  type: 'video',
                  title: 'Article content',
                  sources: [
                    {
                      src: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/ArticleSingle/Article_1_BrowseContent.mp4',
                      type: 'video/mp4'
                    }
                  ]
                }
              },
              {
                title: 'You are now ready to explore the Clear Assured and start planning your ED&I journey.',
                text: '<p>Thank you for taking the time to get familiar with the application.</p>',
                media: {
                  type: 'image',
                  alt: 'Random cheerful image (the one in the design)',
                  resource: 'https://api.stage.clearassured.co.uk/uploads/tour-wizard/onboarding-tour/TourCompleted/TourComplete.jpeg'
                }
              }
            ]
          }
        ]
      }
    },
    computed: {
      ...mapGetters([]),
      tour () {
        const tour = this.tours.find(t => t.id === this.id)
        if (!tour) {
          console.warn('Invalid tour id provided in the ClearAssured tour wizard.')
          return null
        }
        return tour
      }
    },
    methods: {
      ...mapActions([])
    },
    components: {
      TourWizard
    },
    beforeMount () {},
    created () {
      this.id = this.$route.params.id
    },
    mounted () {},
    destroyed () {},
    watch: {
      '$route.params.id' (value) {
        if (value) {
          this.id = this.$route.params.id
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// TourWizardWrapper.vue?0c345b2b